import React, { useContext, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import LeftSidePopupLayout from "../../../../component/popup/modal-layout";
import XCloseIcon from "../../../../component/icons/x-close";
import Style from "./style.module.css";
import { GlobalContext } from "../../../../context/Context";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
} from "../../../../component/form/form-layout";
import { Loader2 } from "lucide-react";

const AddWalletTransaction = ({
  walletId = null,
  currentBalance = null, // ✅ new prop
  onSubmit = () => { },
  onClose = () => { },
  onError = () => { },
}) => {
  // console.log("walletId", walletId)
  // console.log("currentBalance", currentBalance)

  const { state } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const validationSchema = yup.object({
    type: yup.string().required("Please select transaction type"),
    amount: yup
      .number()
      .typeError("Enter a valid amount")
      .min(0, "Amount cannot be negative")
      .required("This field is required")
      .test(
        "sufficient-balance",
        "Insufficient balance for this debit transaction",
        function (value) {
          const { type } = this.parent;
          if (type === "debit") {
            return value <= currentBalance;
          }
          return true;
        }
      ),
    reason: yup.string().required("Please enter a reason"),
  });


  const formik = useFormik({
    initialValues: {
      type: "",
      amount: "",
      reason: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const data = {
          wallet_id: walletId,
          type: values.type,
          amount: values.amount,
          reason: values.reason,
        };

        const res = await apiCall({
          method: "post",
          url: "wallet/add-wallet-transactions",
          data,
        });

        resetForm();
        onSubmit(res?.message || "Transaction added successfully!");
      } catch (error) {
        console.error("Error adding transaction:", error);
        onError(error?.response?.data?.message || "Failed to add transaction.");
      }
    },
  });

  // ✅ Live balance preview calculation
  const updatedBalance = useMemo(() => {
    const amt = parseFloat(formik.values.amount || 0);
    if (!formik.values.type) return currentBalance;
    if (formik.values.type === "credit") return currentBalance + amt;
    if (formik.values.type === "debit") return currentBalance - amt;
    return currentBalance;
  }, [formik.values.amount, formik.values.type, currentBalance]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        {/* Header */}
        <div
          className={`${Style.modal_header} ${Style.addMeModal_head} flex justify-between items-center`}
        >
          <h4>Add Wallet Transaction</h4>
          <div onClick={onClose} className={`${Style.close_btn} cursor-pointer`}>
            <XCloseIcon />
          </div>
        </div>

        {/* Body */}
        <div className={`${Style.addMeModal_body}`}>
          <div className={Style.dcmb__card}>
            <div className={Style.dcmb__cardBody}>
              <div className={`${Style.dcmb__cardBody_formArea} pt-3`}>
                <FormArea>
                  {/* Transaction Type */}
                  <FeildWraper width="100%">
                    <FormLabel required>Transaction Type</FormLabel>
                    <div className="flex items-center gap-5">
                      <FormLabel className="!items-center">
                        <input
                          type="radio"
                          name="type"
                          value="credit"
                          checked={formik.values.type === "credit"}
                          onChange={formik.handleChange}
                          className="accent-green-600"
                        />
                        <em className="!mt-0 !text-green-600">Credit</em>
                      </FormLabel>

                      <FormLabel className="!items-center">
                        <input
                          type="radio"
                          name="type"
                          value="debit"
                          checked={formik.values.type === "debit"}
                          onChange={formik.handleChange}
                          className="accent-red-600"
                        />
                        <em className="!mt-0 !text-red-600">Debit</em>
                      </FormLabel>
                    </div>

                    {formik.touched.type && formik.errors.type && (
                      <FeildError>{formik.errors.type}</FeildError>
                    )}
                  </FeildWraper>

                  {/* Amount */}
                  <FeildWraper width="100%">
                    <FormLabel required>Amount</FormLabel>
                    <input
                      type="number"
                      name="amount"
                      placeholder="Enter amount"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="border rounded-lg px-3 py-2 w-full"
                    />
                    {formik.touched.amount && formik.errors.amount && (
                      <FeildError>{formik.errors.amount}</FeildError>
                    )}

                    {/* ✅ Live Balance Preview */}
                    {formik.values.type && formik.values.amount && (
                      <div
                        className="mt-2 flex items-center gap-1 text-sm font-medium"
                      >
                        {formik.values.type === "credit" ? "After Credit: " : "After Debit: "}
                        <em>
                          ${updatedBalance.toFixed(2)}{" "}
                          <em className="text-gray-500 text-xs ml-1">
                            (Current: ${currentBalance.toFixed(2)})
                          </em>
                        </em>
                      </div>
                    )}
                  </FeildWraper>

                  {/* Reason */}
                  <FeildWraper width="100%">
                    <FormLabel required>Reason</FormLabel>
                    <textarea
                      name="reason"
                      rows={4}
                      placeholder="Enter reason for transaction"
                      value={formik.values.reason}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    ></textarea>
                    {formik.touched.reason && formik.errors.reason && (
                      <FeildError>{formik.errors.reason}</FeildError>
                    )}
                  </FeildWraper>
                </FormArea>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={Style.dcmb__cardFoot}>
          <button
            type="reset"
            className={Style.dcmb__cancel}
            onClick={onClose}
          >
            Discard
          </button>
          <button
            type="submit"
            className={Style.dcmb__submit}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <Loader2 className="animate-spin mr-2 h-4 w-4" />
            ) : (
              "Add Transaction"
            )}
          </button>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default AddWalletTransaction;
